import React, { useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../../utils/luxonFormats";
import { schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import { toast } from "react-toastify";
import PaymentActionsDropdown from "./PaymentActionsDropdown";
import useCopyToClipboard from "app/utils/hooks/useCopyToClipboard";
import Tippy from "@tippyjs/react/headless";

interface Payment {
  purchaseId: number;
  productPrice: string;
  productTitle: string;
  purchaseDate: string;
  purchaseStatus: string;
  // Set by the backend only for staff, when the purchase failed.
  failureReason?: string | null;
  failureMessage?: string | null;
  // CN-2683: when the money was collected. Kept after a cancellation, so a
  // subscription's final cycle still reads as paid.
  paidAt?: string | null;
  currency: string;
  purchaseType: string;
  paymentMethod: string;
  collectionType: string;
  invoiceUrl?: string | null;
  sharePaymentUrl?: string | null;
  familyMemberName?: string | null;
}

interface PaymentsTableProps {
  payments: Payment[];
  isCoach?: boolean;
  onPaymentUpdate?: (_updatedPayment: Payment) => void;
  // CN-1323 (CFTB-3286/3337): when true, label each row with the family member it
  // was purchased for. The BE only populates familyMemberName for purchases made
  // for a dependent, so solo accounts/customers stay unlabelled. Set on both the
  // athlete payment history and the coach customer page.
  showFamilyMember?: boolean;
}

function PaymentsTable({
  payments,
  isCoach = false,
  onPaymentUpdate,
  showFamilyMember = false,
}: PaymentsTableProps) {
  const { t } = useTranslation();
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const { copyToClipboard } = useCopyToClipboard();

  const handleViewInvoice = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCopyPaymentLink = (url: string) => {
    copyToClipboard(url);
    toast.success(t("Payment link copied"));
  };

  const handleMarkAsPaid = async (payment: Payment) => {
    try {
      setLoadingId(payment.purchaseId);

      const response = await schedulingFetchPatch<Payment>(
        `/stripe_purchases/${payment.purchaseId}/mark_paid`
      );

      if (onPaymentUpdate) {
        onPaymentUpdate(response);
      }

      toast.success(t("Payment marked as paid"));

    } catch (error) {
      const errorMessage = error?.errors?.[0] || t("An unexpected error has occurred.");
      toast.error(errorMessage);
      console.error("Mark as paid failed:", error);
    } finally {
      setLoadingId(null);
    }
  };

  const handleMarkAsUnpaid = async (payment: Payment) => {
    try {
      setLoadingId(payment.purchaseId);

      const response = await schedulingFetchPatch<Payment>(
        `/stripe_purchases/${payment.purchaseId}/mark_unpaid`
      );

      if (onPaymentUpdate) {
        onPaymentUpdate(response);
      }

      toast.success(t("Payment marked as unpaid"));

    } catch (error) {
      const errorMessage = error?.errors?.[0] || t("An unexpected error has occurred.");
      toast.error(errorMessage);
      console.error("Mark as unpaid failed:", error);
    } finally {
      setLoadingId(null);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "Paid":
        return styles.statusSuccess;
      case "Pending":
        return styles.statusIncomplete;
      case "Refunded":
        return styles.statusIncomplete;
      // CN-2832: signed up before a custom start date; nothing charged yet.
      case "Scheduled":
        return styles.statusIncomplete;
      default:
        return styles.statusFailed;
    }
  };

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case "Paid":
        return t("Success");
      case "Pending":
        return t("Incomplete");
      case "Past_due":
        return t("Past Due");
      case "Cancelled":
        return t("Cancelled");
      case "Refunded":
        return t("Refunded");
      case "Scheduled":
        return t("Scheduled");
      default:
        return t("Failed");
    }
  };

  const showActionsColumn =
    isCoach || payments.some((p) => Boolean(p.invoiceUrl));

  return (
    <div className={styles.paymentsTableContainer}>
      <table className={styles.paymentsTable}>
        <thead>
          <tr className={styles.labelTable}>
            <th>{t("Amount")}</th>
            <th className={styles.descriptionColumn}>{t("Description")}</th>
            <th>{t("Method")}</th>
            <th>{t("Collection")}</th>
            <th>{t("Date")}</th>
            <th className={styles.statusColumn}>{t("Status")}</th>
            {showActionsColumn && <th className={styles.actionsColumn}>{t("Actions")}</th>}
          </tr>
        </thead>
        <tbody>
          {payments.map((payment, index) => {
            const canMarkAsPaid =
              isCoach &&
              payment.purchaseType === 'offline' &&
              ['pending', 'past_due'].includes(payment.purchaseStatus.toLowerCase());

            const canMarkAsUnpaid =
              isCoach &&
              payment.purchaseType === 'offline' &&
              payment.purchaseStatus.toLowerCase() === 'paid';

            const hasInvoiceLink = Boolean(payment.invoiceUrl);
            const hasMenuItems = canMarkAsPaid || canMarkAsUnpaid || hasInvoiceLink;

            return (
              <tr key={index}>
                <td
                  className={
                    payment.purchaseStatus === "Paid" ? styles.boldAmount : ""
                  }
                >
                  {payment?.productPrice} {payment.currency?.toUpperCase()}
                </td>
                <td className={styles.description}>
                  <span className={styles.descriptionText} title={payment.productTitle}>
                    {payment.productTitle}
                  </span>
                  {showFamilyMember && payment.familyMemberName && (
                    <span
                      className={`${styles.familyMember} ${styles.descriptionText}`}
                      title={payment.familyMemberName}
                    >
                      {t("For: {{name}}", { name: payment.familyMemberName })}
                    </span>
                  )}
                </td>
                <td>{payment.paymentMethod || "-"}</td>
                <td>{payment.collectionType || "-"}</td>
                <td className={styles.dateColumn}>{formatDate(payment.purchaseDate)}</td>
                <td className={styles.statusColumn}>
                  {/* CN-2683: cancelling a subscription marks its final cycle
                      cancelled even though that payment went through. Lead
                      with the payment and note the cancellation under it. */}
                  {payment.purchaseStatus === "Cancelled" && payment.paidAt ? (
                    <>
                      <span className={`${styles.statusBadge} ${styles.statusSuccess}`}>
                        {t("Success")}
                      </span>
                      <span className={styles.statusNote}>{t("Cancelled")}</span>
                    </>
                  ) : (
                    <span className={styles.statusWithInfo}>
                      <span
                        className={`${styles.statusBadge} ${getStatusBadgeClass(
                          payment.purchaseStatus,
                        )}`}
                      >
                        {getStatusDisplayText(payment.purchaseStatus)}
                      </span>
                      {payment.failureMessage && (
                        // Tippy renders the bubble in document.body, so the
                        // scrolling table container cannot clip it. It opens
                        // on hover, on focus and on tap.
                        <Tippy
                          placement="top"
                          trigger="mouseenter focus click"
                          zIndex={9999}
                          render={(attrs) => (
                            <div
                              className={styles.failureTooltip}
                              tabIndex={-1}
                              {...attrs}
                            >
                              {payment.failureMessage}
                            </div>
                          )}
                        >
                          <span
                            data-testid="payment-failure-message"
                            className={styles.failureInfo}
                            tabIndex={0}
                            role="img"
                            aria-label={payment.failureMessage}
                          >
                            <i className="ico ico-info" />
                          </span>
                        </Tippy>
                      )}
                    </span>
                  )}
                </td>
                {showActionsColumn && (
                  <td className={styles.actionsColumn}>
                    {hasMenuItems ? (
                      <div className={`${styles.actionWrapper} ${loadingId === payment.purchaseId ? styles.actionDisabled : ''}`}>
                        <PaymentActionsDropdown disabled={loadingId === payment.purchaseId}>
                          <div className={styles.dropdownContent}>
                            {canMarkAsPaid && (
                              <button
                                onClick={() => handleMarkAsPaid(payment)}
                                className={styles.dropdownItem}
                                disabled={loadingId === payment.purchaseId}
                              >
                                <i className="icon-ok" />
                                <span>{t('Mark as Paid')}</span>
                              </button>
                            )}
                            {canMarkAsUnpaid && (
                              <button
                                onClick={() => handleMarkAsUnpaid(payment)}
                                className={styles.dropdownItem}
                                disabled={loadingId === payment.purchaseId}
                              >
                                <i className="icon-undo" />
                                <span>{t('Mark as Unpaid')}</span>
                              </button>
                            )}
                            {hasInvoiceLink && (
                              <>
                                <button
                                  onClick={() => handleViewInvoice(payment.invoiceUrl)}
                                  className={styles.dropdownItem}
                                >
                                  <i className="icon-eye-open" />
                                  <span>{t('View invoice')}</span>
                                </button>
                                {payment.sharePaymentUrl && payment.sharePaymentUrl !== payment.invoiceUrl && (
                                  <button
                                    onClick={() => handleCopyPaymentLink(payment.sharePaymentUrl)}
                                    className={styles.dropdownItem}
                                  >
                                    <i className="icon-share" />
                                    <span>{t('Copy payment link')}</span>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </PaymentActionsDropdown>
                      </div>
                    ) : (
                      <div className={styles.actionMenuDisabled}>
                        <i className="bullets" />
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default PaymentsTable;
